/**
 * Team Management Component
 *
 * Displays and manages agency team members:
 * - List of team members with roles
 * - Invite new team member
 * - Remove team member
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TeamMember, InviteTeamMemberRequest } from '@/types/agency';
import { agencyService } from '@/services/agency.service';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useAuth } from '@/hooks/useAuth';
import { useAgencyGovernance } from '@/hooks/useAgencyGovernance';
import { formatApiError } from '@/services/api';
import { teamQueryKeys } from '@/lib/queryKeys';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { agencyRoleLabelKey } from '@/utils/agencyRole';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Modal } from '@/components/ui/Modal';
import { Checkbox } from '@/components/ui/Checkbox';
import { InviteBrandAccessSection } from './InviteBrandAccessSection';
import { Toggle } from '@/components/ui/Toggle';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/utils/toast';
import { devLog } from '@/utils/logger';
import { isLastReviewerCancel } from '@/stores/lastReviewerConfirm.store';

interface TeamManagementProps {
  teamMembers: TeamMember[];
}

const INVITE_FORM_ID = 'invite-team-member-form';

export const TeamManagement = ({ teamMembers }: TeamManagementProps) => {
  const { t } = useTranslation('agency');
  const { formatDateOnly } = useTimezone();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const agencyId = user?.agencyId ?? null;
  // OWNER/ADMIN only: remove members, reviewer flag, invite admins. Managers cannot edit their own brand assignments.
  const { canManageAgencyGovernance: canGovern } = useAgencyGovernance();
  const canEditAssignments = (memberId: number): boolean => canGovern || memberId !== user?.id;
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [memberToManage, setMemberToManage] = useState<TeamMember | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState<InviteTeamMemberRequest>({
    email: '',
    fullName: '',
    role: 'user',
  });
  // Inviter's effective scope (/me, SSOT staffSeesAllBrands). A limited inviter cannot grant "All brands".
  const inviterSeesAll = canGovern || (user?.agencySeesAllBrands ?? false);
  const [managerSeesAllChoice, setManagerSeesAll] = useState(true);
  const managerSeesAll = inviterSeesAll && managerSeesAllChoice;
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [approvingUserIds, setApprovingUserIds] = useState<Set<number>>(new Set());

  // Auto-open invite modal when ?addMember=1 is present in the URL
  useEffect(() => {
    if (searchParams.get('addMember') === '1') {
      setIsInviteModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('addMember');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const closeInviteModal = (): void => {
    setIsInviteModalOpen(false);
    setFormData({ email: '', fullName: '', role: 'user' });
    setManagerSeesAll(true);
    setSelectedClientIds([]);
  };

  // Invite team member mutation (brand scope travels with the invite, applied atomically by the backend)
  const buildInvitePayload = (): InviteTeamMemberRequest => {
    if (formData.role === 'manager') {
      return managerSeesAll
        ? { ...formData, seesAllBrands: true }
        : { ...formData, seesAllBrands: false, clientIds: selectedClientIds };
    }
    if (formData.role === 'user') return { ...formData, clientIds: selectedClientIds };
    return formData;
  };
  const inviteMutation = useMutation({
    mutationFn: agencyService.inviteTeamMember,
    onSuccess: (_member, sent) => {
      // Decide from the payload actually sent, not from shared form state.
      if ((sent.clientIds?.length ?? 0) > 0) toast.success(t('agency_team_invite_success_with_access'));
      queryClient.invalidateQueries({ queryKey: ['agency', 'team'] });
      closeInviteModal();
    },
  });

  // Remove team member mutation
  const removeMutation = useMutation({
    mutationFn: agencyService.removeTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'team'] });
      setMemberToRemove(null);
    },
  });

  // Toggle a team member's agency-wide reviewer flag
  const reviewerMutation = useMutation({
    mutationFn: ({ userId, isReviewer }: { userId: number; isReviewer: boolean }) =>
      agencyService.updateStaffReviewer(userId, isReviewer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'team'] });
    },
    onError: (error) => {
      if (isLastReviewerCancel(error)) return;
      const apiMessage = formatApiError(error);
      toast.error(apiMessage.statusCode === 403 && apiMessage.message ? apiMessage.message : t('agency_team_reviewer_update_failed'));
      devLog.error('Error updating reviewer flag:', error);
    },
  });

  // Owner/admin switches a MANAGER between all brands and assigned brands only
  const scopeMutation = useMutation({
    mutationFn: ({ userId, seesAllBrands }: { userId: number; seesAllBrands: boolean }) =>
      agencyService.updateStaffBrandScope(userId, seesAllBrands),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['agency', 'team', userId, 'clients'] });
      queryClient.invalidateQueries({ queryKey: ['agency', 'team'] });
    },
    onError: (error) => {
      if (isLastReviewerCancel(error)) return;
      toast.error(t('agency_team_scope_update_failed'));
      devLog.error('Error updating brand scope:', error);
    },
  });

  // Fetch workspaces for client access assignment during invitation
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useQuery({
    queryKey: teamQueryKeys.agency.workspaces(agencyId),
    queryFn: agencyService.getWorkspaces,
    enabled: isInviteModalOpen,
  });

  // Fetch client access data for selected member
  const { data: clientAccessData, isLoading: isLoadingAccess } = useQuery({
    queryKey: ['agency', 'team', memberToManage?.id, 'clients'],
    queryFn: () => agencyService.getTeamMemberClientAccess(memberToManage!.id),
    enabled: !!memberToManage,
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.role === 'manager' && !managerSeesAll && selectedClientIds.length === 0) {
      toast.error(t('agency_team_client_access_required_error'));
      return;
    }
    inviteMutation.mutate(buildInvitePayload());
  };

  const handleRemove = () => {
    if (memberToRemove) {
      removeMutation.mutate(memberToRemove.id);
    }
  };

  const handleToggleClientAccess = async (clientId: number, currentAccess: boolean) => {
    if (!memberToManage) return;

    setIsUpdating(true);
    try {
      if (currentAccess) {
        // Revoke access
        await agencyService.revokeClientAccess(memberToManage.id, clientId);
        toast.success(t('agency_team_access_revoked'));
      } else {
        // Assign access
        await agencyService.assignClientAccess(memberToManage.id, [clientId]);
        toast.success(t('agency_team_access_granted'));
      }

      // Refetch the client access data
      queryClient.invalidateQueries({
        queryKey: ['agency', 'team', memberToManage.id, 'clients']
      });
    } catch (error) {
      if (isLastReviewerCancel(error)) return;
      toast.error(t('agency_team_access_update_failed'));
      devLog.error('Error updating client access:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleReviewer = (userId: number, isReviewer: boolean) => {
    setApprovingUserIds((prev) => new Set(prev).add(userId));
    reviewerMutation.mutate(
      { userId, isReviewer },
      {
        onSettled: () => {
          setApprovingUserIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        },
      }
    );
  };

  const getRoleBadgeColor = (role: string) => {
    const colors = {
      owner: 'bg-red-100 text-red-700',
      admin: 'bg-red-100 text-red-700',
      manager: 'bg-primary-50 text-primary-800',
      user: 'bg-gray-100 text-gray-700',
    };
    return colors[role as keyof typeof colors] || colors.user;
  };

  const getStatusLabel = (status: 'active' | 'pending' | 'inactive'): string => {
    switch (status) {
      case 'active': return t('agency_team_status_active');
      case 'pending': return t('agency_team_status_pending');
      default: return t('agency_team_status_inactive');
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      inactive: 'bg-gray-100 text-gray-700',
    };
    return colors[status as keyof typeof colors] || colors.inactive;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
        <h3 className="text-base sm:text-lg font-bold text-gray-900">{t('agency_team_members_title')}</h3>
        <Button onClick={() => setIsInviteModalOpen(true)} size="sm" className="flex items-center gap-1.5 flex-shrink-0">
          <Icon name={ICONS.PLUS} size="sm" />
          <span className="hidden sm:inline">{t('agency_team_invite_button')}</span>
          <span className="sm:hidden">{t('agency_team_invite_button_short', { defaultValue: 'Invite' })}</span>
        </Button>
      </div>

      {/* Team Members List */}
      <div className="space-y-3">
        {teamMembers.map((member) => (
          <div
            key={member.id}
            className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 hover:shadow-sm transition-shadow"
          >
            {/* Mobile layout */}
            <div className="sm:hidden space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <Icon name={ICONS.USER} size="sm" color="text-primary-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900 truncate">{member.fullName}</div>
                  <div className="text-xs text-gray-600 truncate">{member.email}</div>
                  {member.lastActive && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {t('agency_team_last_active')}: {formatDateOnly(member.lastActive)}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getRoleBadgeColor(member.role)}`}>
                      {t(agencyRoleLabelKey(member.role))}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadgeColor(member.status)}`}>
                      {getStatusLabel(member.status)}
                    </span>
                  </div>
                </div>
                {canGovern && (
                  <button
                    onClick={() => setMemberToRemove(member)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    title={t('agency_team_remove_member_title')}
                  >
                    <Icon name={ICONS.TRASH} size="sm" color="text-red-600" />
                  </button>
                )}
              </div>
              <div
                className={cn(
                  'flex items-center justify-between px-1',
                  approvingUserIds.has(member.id) && 'opacity-50 pointer-events-none'
                )}
              >
                <span className="text-xs text-gray-600">{t('agency_team_reviewer_label')}</span>
                <Toggle
                  ariaLabel={`${t('agency_team_reviewer_label')}: ${member.fullName || member.email}`}
                  checked={member.isReviewer ?? member.canApprove ?? false}
                  onChange={(checked) => handleToggleReviewer(member.id, checked)}
                  disabled={!canGovern}
                />
              </div>
              {member.role !== 'admin' && member.role !== 'owner' && canEditAssignments(member.id) && (
                <button
                  onClick={() => setMemberToManage(member)}
                  className="w-full px-2.5 py-1.5 border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center gap-1.5 text-xs font-medium"
                >
                  <Icon name={ICONS.LINK} size="xs" />
                  {t('agency_team_manage_access_button')}
                </button>
              )}
            </div>

            {/* Desktop layout */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <Icon name={ICONS.USER} size="md" color="text-primary-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{member.fullName}</div>
                  <div className="text-sm text-gray-600">{member.email}</div>
                  {member.lastActive && (
                    <div className="text-xs text-gray-500 mt-1">
                      {t('agency_team_last_active')}: {formatDateOnly(member.lastActive)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                  {t(agencyRoleLabelKey(member.role))}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(member.status)}`}>
                  {getStatusLabel(member.status)}
                </span>
                <div
                  className={cn(
                    'flex items-center gap-2',
                    approvingUserIds.has(member.id) && 'opacity-50 pointer-events-none'
                  )}
                  title={t('agency_team_reviewer_desc')}
                >
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    {t('agency_team_reviewer_label')}
                  </span>
                  <Toggle
                    ariaLabel={`${t('agency_team_reviewer_label')}: ${member.fullName || member.email}`}
                    checked={member.isReviewer ?? member.canApprove ?? false}
                    onChange={(checked) => handleToggleReviewer(member.id, checked)}
                    disabled={!canGovern}
                  />
                </div>
                {member.role !== 'admin' && member.role !== 'owner' && canEditAssignments(member.id) && (
                  <button
                    onClick={() => setMemberToManage(member)}
                    className="px-3 py-1.5 border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors flex items-center gap-2 text-sm font-medium"
                    title={t('agency_team_manage_access_title')}
                  >
                    <Icon name={ICONS.LINK} size="sm" />
                    {t('agency_team_manage_access_button')}
                  </button>
                )}
                {canGovern && (
                  <button
                    onClick={() => setMemberToRemove(member)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('agency_team_remove_member_title')}
                  >
                    <Icon name={ICONS.TRASH} size="sm" color="text-red-600" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {teamMembers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Icon name={ICONS.USERS} size="xl" color="text-gray-300" />
            <p className="mt-4">{t('agency_team_empty_state')}</p>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      <Modal
        open={isInviteModalOpen}
        onOpenChange={(open) => {
          if (open) setIsInviteModalOpen(true);
          else closeInviteModal();
        }}
        title=""
        size="md"
        showCloseButton={false}
        // Footer lives outside the scrollable body so submit/cancel stay visible however tall the form grows
        footer={
          <div className="flex gap-1.5 sm:gap-2 justify-end">
            <CancelButton onClick={closeInviteModal} tooltip={t('agency_team_cancel_button')} disabled={inviteMutation.isPending} />
            <Button
              type="submit"
              form={INVITE_FORM_ID}
              loading={inviteMutation.isPending}
            >
              <Icon name={ICONS.PAPER_PLANE} size="sm" className="mr-2" ariaHidden />
              {t('agency_team_send_invitation_button')}
            </Button>
          </div>
        }
      >
        <form id={INVITE_FORM_ID} onSubmit={handleInvite} className="space-y-3 sm:space-y-5">
          {/* Header with gradient icon */}
          <div className="text-center">
            <div className="mx-auto mb-3 sm:mb-4 h-10 w-10 sm:h-14 sm:w-14 rounded-2xl bg-primary-400 flex items-center justify-center">
              <Icon name={ICONS.USER} size="md" color="text-white" className="sm:!text-xl" ariaHidden />
            </div>
            <h3 className="text-base sm:text-xl font-bold text-gray-900">{t('agency_team_invite_modal_title')}</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('agency_team_invite_modal_subtitle')}</p>
          </div>

          {/* Form Fields */}
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('agency_team_form_fullname_label')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name={ICONS.USER} size="sm" color="text-gray-400" ariaHidden />
                </div>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full pl-10 pr-3 h-[34px] text-xs lg:h-auto lg:pr-4 lg:py-2.5 lg:text-sm border border-gray-300 rounded-xl ring-1 ring-transparent focus:outline-none focus:ring-primary-400 focus:border-primary-400 transition-colors"
                  placeholder="John Doe"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('agency_team_form_email_label')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="inbox" size="sm" color="text-gray-400" ariaHidden />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-3 h-[34px] text-xs lg:h-auto lg:pr-4 lg:py-2.5 lg:text-sm border border-gray-300 rounded-xl ring-1 ring-transparent focus:outline-none focus:ring-primary-400 focus:border-primary-400 transition-colors"
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>
          </div>

          {/* Role Selection Cards */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">
              {t('agency_team_form_role_label')} <span className="text-red-500">*</span>
            </label>
            <div className={cn('grid gap-2 sm:gap-3', canGovern ? 'grid-cols-3' : 'grid-cols-2')}>
              {[
                { value: 'user', label: t('agency_team_role_user_label'), icon: ICONS.USER, description: t('agency_team_role_user_desc') },
                { value: 'manager', label: t('agency_team_role_manager_label'), icon: ICONS.USERS, description: t('agency_team_role_manager_desc') },
                { value: 'admin', label: t('agency_team_role_admin_label'), icon: ICONS.SHIELD, description: t('agency_team_role_admin_desc') },
              ].filter((role) => canGovern || role.value !== 'admin').map((role) => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => {
                    if (formData.role !== role.value) setSelectedClientIds([]);
                    setFormData({ ...formData, role: role.value as 'admin' | 'manager' | 'user' });
                  }}
                  title={role.description}
                  className={cn(
                    'p-2 sm:p-3 rounded-xl border-2 transition-all text-left hover:shadow-sm',
                    formData.role === role.value
                      ? 'border-primary-500 bg-primary-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  )}
                >
                  <Icon
                    name={role.icon}
                    size="sm"
                    color={formData.role === role.value ? 'text-primary-600' : 'text-gray-400'}
                    ariaHidden
                    className="sm:!text-lg"
                  />
                  <div className={cn(
                    'font-medium mt-1.5 sm:mt-2 text-xs sm:text-sm',
                    formData.role === role.value ? 'text-gray-900' : 'text-gray-700'
                  )}>
                    {role.label}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5 leading-tight sm:line-clamp-2">
                    {role.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Manager scope: all brands (default) vs selected brands only */}
          {formData.role === 'manager' && (
            <div className="space-y-2" role="radiogroup" aria-label={t('agency_team_scope_label')}>
              <div className="text-sm font-medium text-gray-700">{t('agency_team_scope_label')}</div>
              <div className={cn('grid gap-2 sm:gap-3', inviterSeesAll ? 'grid-cols-2' : 'grid-cols-1')}>
                {[
                  { value: true, label: t('agency_team_scope_all') },
                  { value: false, label: t('agency_team_scope_assigned') },
                ].filter((opt) => inviterSeesAll || !opt.value).map((opt) => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    role="radio"
                    aria-checked={managerSeesAll === opt.value}
                    onClick={() => setManagerSeesAll(opt.value)}
                    className={cn(
                      'px-3 py-2 rounded-xl border-2 text-xs sm:text-sm font-medium transition-all text-left',
                      managerSeesAll === opt.value
                        ? 'border-primary-500 bg-primary-50 text-gray-900'
                        : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Brand checklist: Specialist (optional) or Manager limited to selected brands (required) */}
          {(formData.role === 'user' || (formData.role === 'manager' && !managerSeesAll)) && (
            <InviteBrandAccessSection
              key={formData.role}
              brands={workspaces}
              isLoading={isLoadingWorkspaces}
              selectedIds={selectedClientIds}
              onChange={setSelectedClientIds}
              required={formData.role === 'manager'}
              defaultExpanded={formData.role === 'manager'}
            />
          )}

          {/* Info Note */}
          <div className="flex items-start gap-2 p-2.5 sm:p-3 bg-soft-mint border border-light-teal-gray rounded-xl">
            <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" className="mt-0.5 flex-shrink-0" ariaHidden />
            <p className="text-[11px] sm:text-xs text-dark-teal leading-relaxed">
              {t('agency_team_invite_info')}
            </p>
          </div>

          {/* Error Message */}
          {inviteMutation.isError && (
            <div className="flex items-center gap-2 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-xl">
              <Icon name={ICONS.CROSS_CIRCLE} size="sm" color="text-red-600" ariaHidden />
              <span className="text-xs sm:text-sm text-red-700">{formatApiError(inviteMutation.error).statusCode === 403 ? formatApiError(inviteMutation.error).message : t('agency_team_invite_error')}</span>
            </div>
          )}

        </form>
      </Modal>

      {/* Client Access Management Modal */}
      <Modal
        open={!!memberToManage}
        onOpenChange={(open) => !open && setMemberToManage(null)}
        title=""
        size="lg"
        showCloseButton={false}
      >
        <div className="space-y-4 sm:space-y-6">
          {/* Header with gradient icon */}
          <div className="text-center">
            <div className="mx-auto mb-3 sm:mb-4 h-10 w-10 sm:h-14 sm:w-14 rounded-2xl bg-primary-400 flex items-center justify-center">
              <Icon name={ICONS.LINK} size="md" color="text-white" className="sm:!text-xl" ariaHidden />
            </div>
            <h3 className="text-base sm:text-xl font-bold text-gray-900">
              {t('agency_team_manage_access_modal_title')} - {memberToManage?.fullName}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {t('agency_team_manage_access_modal_subtitle')}
            </p>
          </div>

          {/* Loading State */}
          {isLoadingAccess && (
            <div className="flex items-center justify-center py-12">
              <Icon name={ICONS.LOADER} size="xl" color="text-primary-600" className="animate-spin" />
            </div>
          )}

          {/* Content */}
          {!isLoadingAccess && clientAccessData && (
            <>
              {/* Manager scope: All brands vs Assigned brands only (owner/admin only; never for users) */}
              {clientAccessData.role === 'MANAGER' && (
                <div className="flex items-center justify-between gap-3 p-2.5 sm:p-3 border border-gray-200 rounded-xl">
                  <div className="text-sm font-medium text-gray-900">{t('agency_team_scope_label')}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {clientAccessData.seesAllBrands ? t('agency_team_scope_all') : t('agency_team_scope_assigned')}
                    </span>
                    <Toggle
                      ariaLabel={t('agency_team_scope_label')}
                      checked={clientAccessData.seesAllBrands}
                      onChange={(checked) => memberToManage && scopeMutation.mutate({ userId: memberToManage.id, seesAllBrands: checked })}
                      disabled={!canGovern || scopeMutation.isPending}
                    />
                  </div>
                </div>
              )}

              {/* Sees-all-brands info (OWNER/ADMIN by role, MANAGER on "All brands") */}
              {clientAccessData.seesAllBrands && (
                <div className="flex items-start gap-2 p-2.5 sm:p-3 bg-soft-mint border border-light-teal-gray rounded-xl">
                  <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" className="mt-0.5 flex-shrink-0" ariaHidden />
                  <p className="text-xs sm:text-sm text-dark-teal leading-relaxed">
                    {t('agency_team_can_enter_all_clients')}
                  </p>
                </div>
              )}

              {/* Clients List */}
              <div className="space-y-2 sm:space-y-3 max-h-[50vh] sm:max-h-96 overflow-y-auto scrollbar-none">
                {clientAccessData.clients.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Icon name={ICONS.INFO} size="lg" color="text-gray-300" />
                    <p className="mt-2 text-sm">{t('agency_team_no_clients')}</p>
                  </div>
                ) : (
                  clientAccessData.clients.map((client) => {
                    const disabled = clientAccessData.seesAllBrands || isUpdating;
                    return (
                      <label
                        key={client.id}
                        className={cn(
                          'w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 transition-all text-left',
                          client.hasAccess
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-300 bg-white hover:border-gray-400',
                          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        )}
                      >
                        <Checkbox
                          checked={client.hasAccess}
                          onCheckedChange={() => !disabled && handleToggleClientAccess(client.id, client.hasAccess)}
                          disabled={disabled}
                          aria-label={client.name}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm sm:text-base font-medium text-gray-900 truncate">{client.name}</div>
                          {client.accessLevel && (
                            <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                              {t('agency_team_access_level')}: {client.accessLevel}
                            </div>
                          )}
                        </div>
                        {client.hasAccess && (
                          <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-primary-600" ariaHidden className="flex-shrink-0" />
                        )}
                      </label>
                    );
                  })
                )}
              </div>

              {/* Updating Indicator */}
              {isUpdating && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
                  <span>{t('agency_team_updating_access')}</span>
                </div>
              )}
            </>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-2">
            <CancelButton onClick={() => setMemberToManage(null)} tooltip={t('agency_team_close_button')} disabled={isUpdating} />
          </div>
        </div>
      </Modal>

      {/* Remove Confirmation Dialog */}
      <ConfirmDialog
        open={!!memberToRemove}
        onOpenChange={(open) => !open && setMemberToRemove(null)}
        onConfirm={handleRemove}
        title={t('agency_team_remove_confirm_title')}
        description={t('agency_team_remove_confirm_description', { name: memberToRemove?.fullName })}
        confirmText={t('agency_team_remove_confirm_button')}
        variant="danger"
        loading={removeMutation.isPending}
      />
    </div>
  );
};
